/**
 * `invoices.*` — list, get, createDraft, updateDraft, issue, void,
 * clone. The CRUD-shaped surface for the sales-invoicing module.
 *
 * Architecture: domain functions in `@flint/domain/sales/invoices` are
 * called by the matching procedures. The procedure does parse → call
 * → dispatch (architecture §6.1) — no business logic here.
 *
 * Money on the wire is a **stringified bigint of paise** (`moneyPaise`
 * helper). The SPA hydrates back to bigint via the helper so float
 * math never enters the round-trip.
 *
 * @see docs/spec/10-sales-invoicing-spec.md — module contract.
 * @see docs/spec/11-new-invoice-screen-spec.md — the composer screen the
 *      `createDraft` / `updateDraft` / `issue` triple powers.
 */
import { z } from "zod";
import { isoDate, moneyPaise, paged, requireDueOnAfterIssuedOn, uuid } from "../_common.js";

/** GSTIN state code prefix — two digits. */
const stateCode = z.string().regex(/^\d{2}$/, "place of supply is a 2-digit GSTIN state code");

/**
 * Status enum mirrors the schema column + the domain `InvoiceStatus`.
 *
 * `reversed` is the auto-applied state when one or more credit notes
 * cumulatively credit the full invoice value (Section 34 of the CGST
 * Act lets a buyer return the supply; this flips the parent invoice
 * out of the receivable pool). Distinct from `void` — voided invoices
 * are pre-tax-liability cancellations, reversed invoices are
 * post-issued reductions.
 */
export const invoiceStatus = z.enum([
  "draft",
  "issued",
  "partially_paid",
  "paid",
  "overdue",
  "void",
  "reversed",
]);

/** Send-channel choices for the issue + send composite. Mirrors MessageBus. */
export const sendChannel = z.enum(["whatsapp", "email", "sms", "print-later", "issue-only"]);

/** Channel choices for `invoices.send` (manual resend) — no `'issue-only'`. */
export const dispatchChannel = z.enum(["whatsapp", "email", "sms", "print-later"]);

const currencyCode = z.string().regex(/^[A-Z]{3}$/, "currency must be a 3-letter ISO code");
const foreignMinor = z
  .string()
  .regex(/^\d+$/, "foreign amount must be a non-negative integer minor-unit string");
const fxRatePaise = z
  .string()
  .regex(/^\d+$/, "FX rate must be paise per one foreign currency unit");

/** Tax treatment (ADR-0019). Drives whether line items carry tax + surfaces
 *  the regulatory paper trail. */
export const taxTreatment = z.enum([
  "taxable_cgst_sgst",
  "taxable_igst",
  "nil_rated",
  "exempt",
  "out_of_scope",
  "export_lut",
  "export_with_igst",
  "sez_supply",
]);

const fxSnapshotInput = z.object({
  currencyCode: currencyCode.default("INR"),
  foreignAmountMinor: foreignMinor.optional(),
  fxRateToInrPaise: fxRatePaise.optional(),
  fxRateDate: isoDate.optional(),
  /** Tax treatment (ADR-0019). Defaults to taxable_cgst_sgst for INR, export_lut for foreign. */
  taxTreatment: taxTreatment.optional(),
});

/**
 * One line as it travels the wire.
 *
 * - `qtyMilli` — quantity * 1000 (3-decimal precision; `1.500 pcs` =
 *   `1500`). The SPA's input control is plain decimal; the form-side
 *   shim converts to millis at submit.
 * - `ratePaise` — paise as a stringified bigint.
 * - `discountBps` / `taxRateBps` — basis points (5% = 500). Matches
 *   the schema columns + the gst-calc inputs.
 */
export const lineItem = z.object({
  /** Linked inventory item, or null/omitted for free-text lines. */
  itemId: uuid.nullable().optional(),
  description: z.string().min(1).max(500),
  hsn: z.string().min(4).max(8),
  unit: z.string().min(1).max(16).default("Nos"),
  qtyMilli: z.number().int().positive(),
  ratePaise: moneyPaise,
  discountBps: z.number().int().min(0).max(10_000).default(0),
  taxRateBps: z.number().int().min(0).max(10_000),
  /**
   * Per-unit rate in invoice-currency minor units (USD cents, AED fils,
   * etc.). Required when the parent invoice's `currencyCode !== 'INR'`,
   * forbidden otherwise. The domain validates the cross-field invariant.
   * ADR-0020.
   */
  rateForeignMinor: foreignMinor.optional(),
});

// ── createDraft ─────────────────────────────────────────────────────

/** Inner shape — kept un-refined so `updateDraft` can `.extend()` it. */
const createDraftInputObject = z
  .object({
    customerId: uuid,
    issuedOn: isoDate,
    dueOn: isoDate,
    placeOfSupply: stateCode,
    lines: z.array(lineItem).min(1),
    customerNote: z.string().max(2000).optional(),
    internalNote: z.string().max(2000).optional(),
    poReference: z.string().max(64).optional(),
    /**
     * Per-invoice declaration text (printed on the PDF's Declaration section).
     * Overrides `companies.defaultInvoiceDeclaration` for this invoice. Empty
     * or undefined → fall back to the company default (or hardcoded LUT for
     * export). Free-form text; the PDF splits on \n into a numbered list.
     */
    declaration: z.string().max(4000).optional(),
    /** Warehouse goods will be dispatched from. Null = fall back to company default. */
    warehouseId: uuid.nullable().optional(),
  })
  .merge(fxSnapshotInput);

/** Wire schema with the dueOn ≥ issuedOn refinement applied. */
export const createDraftInput = requireDueOnAfterIssuedOn(createDraftInputObject);

export const invoiceLineRow = z.object({
  id: uuid,
  position: z.number().int().positive(),
  itemId: uuid.nullable(),
  description: z.string(),
  hsn: z.string(),
  unit: z.string(),
  qtyMilli: z.number().int().positive(),
  ratePaise: moneyPaise,
  discountBps: z.number().int().min(0).max(10_000),
  taxRateBps: z.number().int().min(0).max(10_000),
  grossPaise: moneyPaise,
  discountPaise: moneyPaise,
  taxablePaise: moneyPaise,
  taxPaise: moneyPaise,
  lineTotalPaise: moneyPaise,
  /**
   * Per-line foreign-currency values (ADR-0020). All six fields move
   * together: present for foreign-currency invoices, null for INR. The DB
   * CHECK enforces the all-or-nothing invariant.
   */
  rateForeignMinor: foreignMinor.nullable(),
  grossForeignMinor: foreignMinor.nullable(),
  discountForeignMinor: foreignMinor.nullable(),
  taxableForeignMinor: foreignMinor.nullable(),
  taxForeignMinor: foreignMinor.nullable(),
  lineTotalForeignMinor: foreignMinor.nullable(),
});

export const invoiceRow = z.object({
  id: uuid,
  /** Empty string for drafts; allocated `INV/2026-27/0001` after issue. */
  docNumber: z.string(),
  customerId: uuid,
  issuedOn: isoDate,
  dueOn: isoDate,
  subtotalPaise: moneyPaise,
  cgstPaise: moneyPaise,
  sgstPaise: moneyPaise,
  igstPaise: moneyPaise,
  totalPaise: moneyPaise,
  currencyCode,
  foreignAmountMinor: foreignMinor.nullable(),
  fxRateToInrPaise: fxRatePaise.nullable(),
  fxRateDate: isoDate.nullable(),
  /** Tax treatment (ADR-0019). */
  taxTreatment,
  placeOfSupply: stateCode,
  status: invoiceStatus,
  /** Customer-facing note printed on the invoice. Null when unset. (PR-B.) */
  customerNote: z.string().nullable(),
  /** Internal note — never sent to the customer. (PR-B.) */
  internalNote: z.string().nullable(),
  /** Customer's PO reference. (PR-B.) */
  poReference: z.string().nullable(),
  /** Per-invoice Declaration override (migration 0035). Null = use the
   *  company default; null + non-export = no declaration block. */
  declaration: z.string().nullable(),
  /** Warehouse goods are dispatched from. Null = company default warehouse. */
  dispatchWarehouseId: uuid.nullable(),
});

export const createDraftOutput = z.object({
  invoice: invoiceRow,
  lines: z.array(invoiceLineRow),
});

// ── import (batch create drafts from CSV) ─────────────────────────

/**
 * One import row = one draft invoice (with all its lines inline).
 *
 * The client parses the CSV, groups rows by `sourceRef`, and posts one
 * `importRow` per resulting invoice. The server treats each row as a
 * standalone `createDraft` — customer must exist, dates + place-of-supply
 * are validated, lines are already normalised to milli/paise/bps.
 *
 * `sourceRef` is a human-supplied idempotency key (e.g. the caller's
 * legacy invoice number). Rows that duplicate a sourceRef already imported
 * into the same workspace are skipped with `duplicate_source_ref`.
 */
export const importRow = createDraftInputObject.extend({
  /**
   * Idempotency key from the source system (max 60 chars — persisted on
   * `invoices.po_reference`). Re-uploading a row with the same `sourceRef`
   * skips instead of creating a duplicate draft.
   */
  sourceRef: z.string().min(1).max(60),
});

export const importInput = z.object({
  /** At least one, at most 500 rows per batch — V1 sync cap. */
  rows: z.array(importRow).min(1).max(500),
});

export const importOutput = z.object({
  imported: z.number().int().nonnegative(),
  skipped: z.array(
    z.object({
      /** 0-based index into `importInput.rows`. */
      rowIndex: z.number().int().nonnegative(),
      reason: z.enum([
        "duplicate_source_ref",
        "customer_not_found",
        "validation",
        "internal_error",
      ]),
      /** Human-readable summary for the toast / detail sheet. */
      message: z.string(),
    }),
  ),
});

// ── updateDraft ─────────────────────────────────────────────────────

export const updateDraftInput = requireDueOnAfterIssuedOn(
  createDraftInputObject.extend({ draftId: uuid }),
);
export const updateDraftOutput = createDraftOutput;

// ── issue ───────────────────────────────────────────────────────────

/**
 * Two shapes:
 *   - `{ draftId }` — promote an existing draft.
 *   - `{ customerId, lines, ... }` — inline create-and-issue.
 *
 * The composer's "Save draft" + "Issue" buttons send the first shape;
 * the prototype's "Issue and send via WhatsApp" CTA sends either
 * (depending on whether autosave already created a draft).
 *
 * `sendVia` is optional. When supplied (and not `'issue-only'`), the
 * router fires `sendInvoice` after the issue tx commits — same effect
 * as calling `invoices.send` separately, but in one round-trip.
 */
const issueDraftPath = requireDueOnAfterIssuedOn(
  z.object({
    draftId: uuid,
    issuedOn: isoDate,
    dueOn: isoDate,
    placeOfSupply: stateCode,
    sendVia: sendChannel.optional(),
    /**
     * Resubmit token (ADR-0031). When a configured `invoice_discount` chain
     * gated the issue, the operator approves in the inbox and resends with the
     * `pending_approvals.id` here (always paired with the gated `draftId`).
     */
    approvalId: uuid.optional(),
  }),
);
const issueInlinePath = requireDueOnAfterIssuedOn(
  z
    .object({
      customerId: uuid,
      issuedOn: isoDate,
      dueOn: isoDate,
      placeOfSupply: stateCode,
      lines: z.array(lineItem).min(1),
      customerNote: z.string().max(2000).optional(),
      internalNote: z.string().max(2000).optional(),
      poReference: z.string().max(64).optional(),
      declaration: z.string().max(4000).optional(),
      /** Warehouse goods are dispatched from. Null = fall back to company default. */
      warehouseId: uuid.nullable().optional(),
      sendVia: sendChannel.optional(),
    })
    .merge(fxSnapshotInput),
);

export const issueInput = z.union([issueDraftPath, issueInlinePath]);

export const issueOutput = z.object({
  /** `issued` = numbered + finalized; `awaiting_approval` = a discount chain is pending. */
  outcome: z.enum(["issued", "awaiting_approval"]),
  /** The issued invoice; null while awaiting approval (the draft is untouched). */
  invoice: invoiceRow.nullable(),
  lines: z.array(invoiceLineRow),
  /** The draft awaiting approval — resubmit with this `draftId` + `approvalId`. Null when issued. */
  draftId: uuid.nullable(),
  /** Filed `pending_approvals.id` when `outcome='awaiting_approval'`, else null. */
  approvalId: uuid.nullable(),
  /** Populated when `sendVia` was supplied and the send fired. Always null while awaiting. */
  send: z
    .object({
      channel: dispatchChannel,
      invoiceSendId: uuid,
      notificationId: uuid.nullable(),
      deduplicated: z.boolean(),
    })
    .nullable(),
});

// ── send (manual resend or first send for issue-only invoices) ──────

export const sendInput = z.object({
  invoiceId: uuid,
  channel: dispatchChannel,
});

export const sendOutput = z.object({
  invoiceSendId: uuid,
  notificationId: uuid.nullable(),
  deduplicated: z.boolean(),
  channel: dispatchChannel,
});

// ── void ────────────────────────────────────────────────────────────

export const voidInput = z.object({
  invoiceId: uuid,
  /** Mandatory per system-decisions §12 — lands in the audit log. */
  reason: z.string().trim().min(3, "Reason is required.").max(500),
});

export const voidOutput = z.object({
  invoiceId: uuid,
  status: z.literal("void"),
});

// ── clone ───────────────────────────────────────────────────────────

export const cloneInput = z.object({ invoiceId: uuid });
export const cloneOutput = createDraftOutput;

// ── deleteDraft ─────────────────────────────────────────────────────

/**
 * Abandon a draft. Refused server-side for non-drafts (issued
 * invoices use void). The wire payload is just the id; the row's
 * tenant scope is enforced by RLS.
 */
export const deleteDraftInput = z.object({ invoiceId: uuid });
export const deleteDraftOutput = z.object({
  invoiceId: uuid,
  customerId: uuid,
  totalPaise: moneyPaise,
});

// ── get ─────────────────────────────────────────────────────────────

export const getInput = z.object({ invoiceId: uuid });

export const getOutput = z.object({
  invoice: invoiceRow,
  lines: z.array(invoiceLineRow),
  customer: z.object({
    id: uuid,
    name: z.string(),
    gstin: z.string().nullable(),
    state: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    /** Billing address — rendered on the printed-invoice "Bill to" block.
     *  Foreign/export buyers carry `country` instead of an Indian `state`. */
    addressLine1: z.string().nullable(),
    addressLine2: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    /** Drives the receipt dialog's TDS pre-fill. (PR-C, reading PR-B fields.) */
    isTdsApplicable: z.boolean(),
    tdsSection: z.string().nullable(),
  }),
});

// ── list ────────────────────────────────────────────────────────────

export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  status: invoiceStatus.optional(),
  customerId: uuid.optional(),
  dateFrom: isoDate.optional(),
  dateTo: isoDate.optional(),
  /** Free-text on doc number + customer name. */
  search: z.string().max(120).optional(),
});

const listRow = z.object({
  id: uuid,
  /** `null` for drafts; `INV/2026-27/0001` after issue. */
  docNumber: z.string().nullable(),
  customerId: uuid,
  customerName: z.string(),
  customerGstin: z.string().nullable(),
  /** City on the customer's billing address. Drives the row sub-line
   *  ("Mumbai · GST"). Null when unset. */
  customerCity: z.string().nullable(),
  customerState: z.string().nullable(),
  /** Surfaced so the hero "Top exposure" card can build a `tel:` link
   *  without an extra customer fetch. Null when the customer row has
   *  no phone on file. */
  customerPhone: z.string().nullable(),
  /** True when the customer has a GSTIN on file. The actual GSTIN
   *  string is intentionally NOT in the list — fetch the detail if
   *  you need it. */
  customerGstRegistered: z.boolean(),
  issuedOn: isoDate,
  dueOn: isoDate,
  totalPaise: moneyPaise,
  /**
   * Cumulative credit-note total against this invoice. Drives the
   * Amount-column strike-through + net display on the list. `'0'` when
   * no CNs exist.
   */
  creditedPaise: moneyPaise,
  currencyCode,
  foreignAmountMinor: foreignMinor.nullable(),
  fxRateToInrPaise: fxRatePaise.nullable(),
  fxRateDate: isoDate.nullable(),
  status: invoiceStatus,
});

export const listOutput = paged(listRow).extend({
  /** Per-status counts for the page-header tabs. Same filters as the
   *  list, MINUS the status filter — each tab shows what it'd land on. */
  counts: z.object({
    all: z.number().int().nonnegative(),
    draft: z.number().int().nonnegative(),
    issued: z.number().int().nonnegative(),
    partially_paid: z.number().int().nonnegative(),
    paid: z.number().int().nonnegative(),
    overdue: z.number().int().nonnegative(),
    void: z.number().int().nonnegative(),
    reversed: z.number().int().nonnegative(),
  }),
  /**
   * Page-header KPI strip. Same filter set MINUS the status filter so
   * the KPIs reflect the full visible scope, not just the current
   * tab. `outstandingPaise` approximates "money still owed" by
   * summing issued + partially_paid + overdue totals — receipts
   * vertical (V1.x) will subtract received amounts.
   */
  kpis: z.object({
    outstandingPaise: moneyPaise,
    overduePaise: moneyPaise,
    paidThisMonthPaise: moneyPaise,
    draftCount: z.number().int().nonnegative(),
    outstandingCustomerCount: z.number().int().nonnegative(),
    overdueCustomerCount: z.number().int().nonnegative(),
    overdueInvoiceCount: z.number().int().nonnegative(),
    oldestOverdueDays: z.number().int().nullable(),
    /**
     * Signed bigint as a stringified number (carries the sign — Δ may be
     * negative). PR-F addendum.
     */
    outstanding7dDeltaPaise: z.string().regex(/^-?\d+$/, "signed paise"),
    paidLastMonthPaise: moneyPaise,
  }),
});

// ── Inferred types (re-exported for the API + SPA) ──────────────────

export type LineItem = z.infer<typeof lineItem>;
export type CreateDraftInput = z.infer<typeof createDraftInput>;
export type CreateDraftOutput = z.infer<typeof createDraftOutput>;
export type UpdateDraftInput = z.infer<typeof updateDraftInput>;
export type UpdateDraftOutput = z.infer<typeof updateDraftOutput>;
export type IssueInput = z.infer<typeof issueInput>;
export type IssueOutput = z.infer<typeof issueOutput>;
export type VoidInput = z.infer<typeof voidInput>;
export type VoidOutput = z.infer<typeof voidOutput>;
export type CloneInput = z.infer<typeof cloneInput>;
export type CloneOutput = z.infer<typeof cloneOutput>;
export type DeleteDraftInput = z.infer<typeof deleteDraftInput>;
export type DeleteDraftOutput = z.infer<typeof deleteDraftOutput>;
export type SendInput = z.infer<typeof sendInput>;
export type SendOutput = z.infer<typeof sendOutput>;
export type DispatchChannel = z.infer<typeof dispatchChannel>;
export type GetInput = z.infer<typeof getInput>;
export type GetOutput = z.infer<typeof getOutput>;
export type ListInput = z.infer<typeof listInput>;
export type ListOutput = z.infer<typeof listOutput>;
export type InvoiceRow = z.infer<typeof invoiceRow>;
export type InvoiceLineRow = z.infer<typeof invoiceLineRow>;
export type InvoiceStatus = z.infer<typeof invoiceStatus>;
export type SendChannel = z.infer<typeof sendChannel>;
export type ImportRow = z.infer<typeof importRow>;
export type ImportInput = z.infer<typeof importInput>;
export type ImportOutput = z.infer<typeof importOutput>;
